import React from 'react';
import {
  ContractPackageHashFilter,
  HashFilterWithCsprName,
  InfoSvgIcon,
  PageDataTable,
  TabDataTable,
} from '../../../components';
import { InfoIcon, TableRow, TableRowType } from '@make-software/cspr-design';
import { TableDataHeader } from '@block-explorer/ui';
import { Trans, useTranslation } from 'react-i18next';
import TokenTxsItem from '../../../components/token-transaction-item/token-transaction-item';
import { useGetPaginatedAccountCep18TxsList } from '../../../hooks/event-store-hooks';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';

interface AccountTokensTxsListProps {
  accountHash: string;
  publicKey?: string;
  pageSize?: number;
  showTopPagination?: boolean;
}

export const AccountTokensTxsList = ({
  accountHash,
  publicKey,
  pageSize = 10,
  showTopPagination,
}: AccountTokensTxsListProps) => {
  const { t } = useTranslation();

  const currency = useSelector(
    (state: RootState) => state.currencies.selectedCurrency
  );

  const {
    data: accountTokensTxs,
    loading: accountTokensTxsLoading,
    error,
    paginationProps,
    sortingProps,
    filterProps,
  } = useGetPaginatedAccountCep18TxsList(
    accountHash
      ? {
          accountHash,
          includes: `rate(${currency.id}),deploy,contract_package,from_account_info,to_account_info,from_centralized_account_info,to_centralized_account_info,from_cspr_name,to_cspr_name`,
          defaultPageSize: pageSize,
        }
      : null
  );
  const TableComponent = showTopPagination ? PageDataTable : TabDataTable;

  return (
    <TableComponent
      loading={accountTokensTxsLoading}
      error={error}
      data={accountTokensTxs}
      paginationProps={paginationProps}
      sortingProps={sortingProps}
      tableRowType={TableRowType.TextWithAvatar}
      renderDataHeader={({ sortingProps }) => (
        <TableRow>
          <TableDataHeader
            fixedWidthRem={11.25}
            filter={<ContractPackageHashFilter {...filterProps} />}
          >
            <Trans t={t}>Token</Trans>
          </TableDataHeader>
          <TableDataHeader fixedWidthRem={8.75}>
            {t('Transaction Hash')}
          </TableDataHeader>
          <TableDataHeader
            reverseSortingDirection
            sortableColumnField={'timestamp'}
            {...sortingProps}
            fixedWidthRem={5.25}
          >
            <Trans t={t}>Age</Trans>
          </TableDataHeader>
          <TableDataHeader fixedWidthRem={4.375}>Action</TableDataHeader>
          <TableDataHeader
            fixedWidthRem={11.875}
            filter={
              <HashFilterWithCsprName {...filterProps} filterId={'from_hash'} />
            }
          >
            From
          </TableDataHeader>
          <TableDataHeader
            fixedWidthRem={11.875}
            filter={
              <HashFilterWithCsprName {...filterProps} filterId={'to_hash'} />
            }
          >
            To
          </TableDataHeader>
          <TableDataHeader align="right" fixedWidthRem={8.75}>
            Amount
          </TableDataHeader>
          <TableDataHeader
            align="right"
            icon={<InfoSvgIcon src={InfoIcon} />}
            tooltipText={t(
              `CSPR tokens spent for executing the transaction. Calculated as the transaction payment minus the partial refund of unused gas. A 75% refund is provided on unused gas for successfully executed non-native transactions.`
            )}
          >
            <Trans t={t}>Charge</Trans>
          </TableDataHeader>
        </TableRow>
      )}
      renderPaginatedData={(transactions) =>
        transactions.map((data, idx) => (
          <TokenTxsItem
            data={data}
            loading={accountTokensTxsLoading}
            showCallerInfo={false}
            isOutgoingAmount={
              data.from_public_key === publicKey ||
              data.from_hash === accountHash ||
              false
            }
          />
        ))
      }
    />
  );
};
